import { useState } from 'react';
import { ChevronDown, Plus, Trash2 } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Table,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { FormExportButton } from 'components/IAP/FormExportButton';
import { useIcsFormPdfPreview } from 'components/IAP/context/IcsFormPdfPreviewContext';
import FormVersioningPanel from 'components/IAP/femaCards/FormVersioningPanel';
import { buildFormCompletion } from 'components/IAP/femaCards/formSignatureCompletion';
import {
  useAuthoredRows,
  useCreateAuthoredRow,
  useDeleteAuthoredRow,
  useUpdateAuthoredRow,
} from 'components/IAP/hooks/icsAuthoredFormHooks';
import {
  useFormVersionTip,
  useRecordFormVersionSave,
  type IcsFormSignatureSlot,
} from 'components/IAP/hooks/icsFormVersionHooks';
import { useIncident } from 'components/IAP/hooks/incidentHooks';
import { useIncidentInfo } from 'components/IAP/hooks/incidentInfoHooks';
import { useICSContext } from 'components/IAP/planningView/ICSContext';

import { Ics204CardSection } from '../AssignmentList/ics204BoxLabels';
import {
  parseWalaSignatures,
  type WalaSignature,
} from '../AssignmentList/ics204SignatureSlots';
import { PrintedValue } from '../icsBoxPrimitives';
import SaveableField from '../SaveableField';
import {
  ICS225_BOX_LABELS,
  ICS225_MEASURED_STANDARDS,
  ICS225_RATING_FACTORS,
} from '../ics225BoxLabels';
import { NewCgFormCard } from './newCgFormBoxes';

const BASE_ID = 'ICS_225';

/** The row's summary columns, which is what the table shows collapsed. */
const SUMMARY_COLUMNS = ['Rated Individual', 'Position Assigned', 'Signatures'];

/**
 * ICS 225-CG, boxed as the sheet prints it.
 *
 * Unlike the rest of the Coast Guard set this sheet is about a person rather
 * than an operational period, so an incident carries several at once. They are
 * listed like the 204s: one collapsed row per rating, expanding to the whole
 * numbered form, and each rating signed and versioned on its own rather than
 * sharing the card's.
 *
 * Boxes 2 and 4 are the incident's own name and location, so they are printed
 * rather than typed; the remaining boxes belong to the rating.
 */
const Ics225Card = () => {
  const { isExporting } = useIcsFormPdfPreview();
  const { data: ratings = [] } = useAuthoredRows('performanceRatings');
  const createRow = useCreateAuthoredRow('performanceRatings');

  return (
    <div data-tour="demob-ics225">
    <NewCgFormCard
      title="Personnel Performance Rating ICS-225"
      formname="ICS_225_CG"
      exportLabel="Preview ICS-225"
    >
      <div className="tw:flex tw:justify-start">
        <Button
          type="button"
          size="sm"
          disabled={createRow.isPending}
          onClick={() => createRow.mutate({ ratings: {} })}
        >
          <Plus className="tw:size-4" />
          Add Rating
        </Button>
      </div>

      <Table data-testid="ics225-ratings-table">
        <TableHeader>
          <TableRow>
            <TableHead className="tw:w-8" />
            {SUMMARY_COLUMNS.map((column) => (
              <TableHead key={column}>{column}</TableHead>
            ))}
            <TableHead className="tw:w-24" />
          </TableRow>
        </TableHeader>
        {ratings.map((rating) => (
          <Ics225RatingRow
            key={String(rating.rating_id ?? rating.id ?? '')}
            rating={rating}
            columnCount={SUMMARY_COLUMNS.length + 2}
            isExporting={isExporting}
          />
        ))}
      </Table>
    </NewCgFormCard>
    </div>
  );
};

/**
 * One rating: a summary row that expands into the printed boxes.
 *
 * A tbody per rating rather than a fragment, so the expanded detail can span
 * the table without the summary row losing its columns — the same shape the
 * 204 list uses.
 */
const Ics225RatingRow = ({
  rating,
  columnCount,
  isExporting,
}: {
  rating: Record<string, unknown>;
  columnCount: number;
  isExporting: boolean;
}) => {
  const [expanded, setExpanded] = useState(false);

  const { exportAsForm } = useIcsFormPdfPreview();
  const { effectivePeriodNumber, readOnlyForms } = useICSContext();
  const incident = useIncident();
  const { data: incidentInfo } = useIncidentInfo();
  const updateRow = useUpdateAuthoredRow('performanceRatings');
  const deleteRow = useDeleteAuthoredRow('performanceRatings');

  const entityId = String(rating.rating_id ?? rating.id ?? '');
  const prefix = `ics225-${entityId}`;

  const recordVersionSave = useRecordFormVersionSave(
    BASE_ID,
    effectivePeriodNumber,
    entityId
  );
  const { data: tip } = useFormVersionTip(
    BASE_ID,
    effectivePeriodNumber,
    entityId
  );

  const signatures = parseWalaSignatures(tip?.signatures);
  const completion = buildFormCompletion(tip?.signatureSlots ?? [], signatures);

  const scores = (rating.ratings ?? {}) as Record<string, string>;
  const incidentLocation =
    incidentInfo?.incidentLocation || incident.data?.address || '';

  const save = (patch: Record<string, unknown>) =>
    updateRow.mutate(
      { entityId, ...patch },
      { onSuccess: () => recordVersionSave.mutate() }
    );

  /** A typed box holding one blank. */
  const textBox = (
    boxLabel: string,
    key: string,
    placeholder?: string,
    multiline = false
  ) => (
    <Ics204CardSection
      boxLabel={boxLabel}
      testId={`${prefix}-section-${key}`}
      className="tw:mt-0 tw:h-full tw:min-w-0"
    >
      <SaveableField
        id={`${prefix}-${key}`}
        label={boxLabel}
        hideLabel
        multiline={multiline}
        placeholder={placeholder}
        value={String(rating[key] ?? '')}
        onSave={(value) => save({ [key]: value })}
      />
    </Ics204CardSection>
  );

  return (
    <tbody data-testid="ics225-rating">
      <TableRow
        className="tw:cursor-pointer"
        aria-expanded={expanded}
        onClick={() => setExpanded((prev) => !prev)}
      >
        <TableCell className="tw:w-8">
          <ChevronDown
            className={cn(
              'tw:size-4 tw:transition-transform',
              expanded && 'tw:rotate-180'
            )}
          />
        </TableCell>
        <TableCell>{String(rating.rated_name ?? '') || '—'}</TableCell>
        <TableCell>{String(rating.position_assigned ?? '') || '—'}</TableCell>
        <TableCell data-testid={`${prefix}-signature-status`}>
          {completion.requiredTotal === 0
            ? '—'
            : `${completion.completedCount} of ${completion.requiredTotal} signed`}
        </TableCell>
        <TableCell
          className="tw:text-right"
          onClick={(event) => event.stopPropagation()}
        >
          <Button
            type="button"
            variant="ghost"
            size="icon"
            disabled={readOnlyForms}
            aria-label={`Delete the rating for ${String(rating.rated_name ?? '') || 'this person'}`}
            onClick={() => deleteRow.mutate(entityId)}
          >
            <Trash2 className="tw:size-4" />
          </Button>
        </TableCell>
      </TableRow>

      {expanded ? (
        <TableRow className="tw:hover:bg-transparent">
          <TableCell
            colSpan={columnCount}
            className="tw:h-auto tw:min-w-0 tw:whitespace-normal tw:align-top"
          >
            <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-3">
              <FormVersioningPanel
                baseId={BASE_ID}
                periodNumber={effectivePeriodNumber}
                readOnly={readOnlyForms}
                formInstanceId={entityId}
              />

              <div className="tw:grid tw:min-w-0 tw:grid-cols-1 tw:gap-3 tw:md:grid-cols-2">
                {textBox(ICS225_BOX_LABELS.ratedName, 'rated_name')}

                <Ics204CardSection
                  boxLabel={ICS225_BOX_LABELS.incidentName}
                  testId={`${prefix}-section-incident-name`}
                  className="tw:mt-0 tw:h-full tw:min-w-0"
                >
                  <PrintedValue
                    value={incident.data?.name ?? ''}
                    testId={`${prefix}-field-incident-name`}
                  />
                </Ics204CardSection>

                {textBox(
                  ICS225_BOX_LABELS.contact,
                  'contact',
                  'Phone number or email'
                )}

                <Ics204CardSection
                  boxLabel={ICS225_BOX_LABELS.incidentLocation}
                  testId={`${prefix}-section-incident-location`}
                  className="tw:mt-0 tw:h-full tw:min-w-0"
                >
                  <PrintedValue
                    value={incidentLocation}
                    testId={`${prefix}-field-incident-location`}
                  />
                </Ics204CardSection>
              </div>

              <div className="tw:grid tw:min-w-0 tw:grid-cols-1 tw:gap-3 tw:md:grid-cols-3">
                {textBox(
                  ICS225_BOX_LABELS.positionAssigned,
                  'position_assigned'
                )}

                <Ics204CardSection
                  boxLabel={ICS225_BOX_LABELS.datesOfAssignment}
                  testId={`${prefix}-section-dates-of-assignment`}
                  className="tw:mt-0 tw:h-full tw:min-w-0"
                >
                  <div className="tw:grid tw:gap-2">
                    <SaveableField
                      id={`${prefix}-assignment-from`}
                      label="From"
                      type="date"
                      value={String(rating.assignment_from ?? '')}
                      onSave={(value) => save({ assignment_from: value })}
                    />
                    <SaveableField
                      id={`${prefix}-assignment-to`}
                      label="To"
                      type="date"
                      value={String(rating.assignment_to ?? '')}
                      onSave={(value) => save({ assignment_to: value })}
                    />
                  </div>
                </Ics204CardSection>

                <Ics204CardSection
                  boxLabel={ICS225_BOX_LABELS.incidentStartDate}
                  testId={`${prefix}-section-incident-start-date`}
                  className="tw:mt-0 tw:h-full tw:min-w-0"
                >
                  <PrintedValue
                    value={String(incident.data?.incident_created ?? '')}
                    testId={`${prefix}-field-incident-start-date`}
                  />
                </Ics204CardSection>
              </div>

              <div className="tw:grid tw:min-w-0 tw:grid-cols-1 tw:gap-3 tw:md:grid-cols-2">
                {textBox(ICS225_BOX_LABELS.incidentType, 'incident_type')}
                {textBox(ICS225_BOX_LABELS.incidentKind, 'incident_kind')}
              </div>

              <Ics204CardSection
                boxLabel={ICS225_BOX_LABELS.evaluation}
                testId={`${prefix}-section-evaluation`}
                className="tw:mt-0"
              >
                <table className="tw:w-full tw:text-sm">
                  <thead>
                    <tr>
                      <th className="tw:px-2 tw:py-1 tw:text-left tw:font-normal tw:text-muted-foreground">
                        Rating Factors
                      </th>
                      <th className="tw:px-2 tw:py-1 tw:text-left tw:font-normal tw:text-muted-foreground">
                        Rating Standard
                      </th>
                      <th className="tw:px-2 tw:py-1 tw:text-left tw:font-normal tw:text-muted-foreground">
                        Measured Standards
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {ICS225_RATING_FACTORS.map((factor) => (
                      <tr key={factor.key}>
                        <td className="tw:px-2 tw:py-2 tw:align-top tw:whitespace-nowrap">
                          {factor.letter}. {factor.label}
                        </td>
                        <td className="tw:px-2 tw:py-2 tw:align-top tw:text-muted-foreground">
                          {factor.standard}
                        </td>
                        <td className="tw:px-2 tw:py-2 tw:align-top">
                          <RadioGroup
                            className="tw:flex tw:gap-3"
                            value={scores[factor.key] ?? ''}
                            onValueChange={(value) =>
                              save({
                                ratings: { ...scores, [factor.key]: value },
                              })
                            }
                          >
                            {ICS225_MEASURED_STANDARDS.map((standard) => (
                              <label
                                key={standard}
                                className="tw:flex tw:items-center tw:gap-1 tw:whitespace-nowrap"
                              >
                                <RadioGroupItem
                                  value={standard}
                                  aria-label={`${factor.label} ${standard}`}
                                />
                                {standard}
                              </label>
                            ))}
                          </RadioGroup>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Ics204CardSection>

              {textBox(
                ICS225_BOX_LABELS.accomplishments,
                'accomplishments',
                'Enter responsibilities and notable accomplishments...',
                true
              )}

              <div className="tw:grid tw:min-w-0 tw:grid-cols-1 tw:gap-3 tw:md:grid-cols-2">
                {textBox(
                  ICS225_BOX_LABELS.supervisorPosition,
                  'supervisor_position'
                )}
                {textBox(ICS225_BOX_LABELS.supervisorName, 'supervisor_name')}
              </div>

              <Ics225SignatureBoxes
                prefix={prefix}
                slots={tip?.signatureSlots ?? []}
                signatures={signatures}
              />

              <FormExportButton
                formname="ICS_225_CG"
                onExport={(format) =>
                  void exportAsForm({
                    formname: 'ICS_225_CG',
                    data: { ratingId: entityId },
                    mapSketch: null,
                    format,
                  })
                }
                label="Preview this rating"
                disabled={isExporting}
              />
            </div>
          </TableCell>
        </TableRow>
      ) : null}
    </tbody>
  );
};

/**
 * Boxes 12, 13, 16 and 17, which are now signature slots rather than blanks.
 *
 * Read-only: signing happens in the version bar above, and these print who
 * signed and when the way the paper form records it. The supervisor signs
 * first, because signing box 16 is what freezes the rating for the rated
 * individual to acknowledge in box 12.
 */
const Ics225SignatureBoxes = ({
  prefix,
  slots,
  signatures,
}: {
  prefix: string;
  slots: IcsFormSignatureSlot[];
  signatures: WalaSignature[];
}) => {
  const box = (
    slotId: string,
    signatureLabel: string,
    dateLabel: string,
    testIdKey: string
  ) => {
    const slot = slots.find((candidate) => candidate.id === slotId);
    const signature = signatures.find(
      (candidate) => candidate.role === slot?.role
    );

    return (
      <div
        key={slotId}
        className="tw:grid tw:min-w-0 tw:grid-cols-1 tw:gap-3 tw:md:grid-cols-2"
      >
        <Ics204CardSection
          boxLabel={signatureLabel}
          testId={`${prefix}-section-${testIdKey}-signature`}
          className="tw:mt-0 tw:h-full tw:min-w-0"
        >
          <PrintedValue
            value={signature?.name ?? ''}
            testId={`${prefix}-field-${testIdKey}-signature`}
          />
        </Ics204CardSection>

        <Ics204CardSection
          boxLabel={dateLabel}
          testId={`${prefix}-section-${testIdKey}-date`}
          className="tw:mt-0 tw:h-full tw:min-w-0"
        >
          <PrintedValue
            value={
              signature?.signedAt
                ? new Date(signature.signedAt).toLocaleDateString()
                : ''
            }
            testId={`${prefix}-field-${testIdKey}-date`}
          />
        </Ics204CardSection>
      </div>
    );
  };

  return (
    <>
      {box(
        'ratedPersonSignature',
        ICS225_BOX_LABELS.ratedPersonSignature,
        ICS225_BOX_LABELS.ratedPersonDate,
        'rated-person'
      )}
      {box(
        'supervisorSignature',
        ICS225_BOX_LABELS.supervisorSignature,
        ICS225_BOX_LABELS.supervisorDate,
        'supervisor'
      )}
    </>
  );
};

export default Ics225Card;
